import { COMM_MODE_EMAIL, COMM_MODE_IN_PERSON, COMM_MODE_PHONE, COMM_MODE_TEXT } from 'app/lib/commLog'
import type { PmsCommLogParentKey, PmsCommLogRulePlan, PmsCommLogSavedRule } from 'app/types/pms-comm-log'

export type PmsCommLogGroup = 'conversations' | 'records'

export type PmsCommLogChannel = 'PHONE' | 'EMAIL' | 'TEXT' | 'IN_PERSON'

export const PMS_COMM_LOG_CHANNELS: readonly PmsCommLogChannel[] = [`PHONE`, `EMAIL`, `TEXT`, `IN_PERSON`]

export const PMS_COMM_LOG_CHANNEL_MODES: Readonly<Record<PmsCommLogChannel, number>> = {
  PHONE: COMM_MODE_PHONE,
  EMAIL: COMM_MODE_EMAIL,
  TEXT: COMM_MODE_TEXT,
  IN_PERSON: COMM_MODE_IN_PERSON,
}

export const PMS_COMM_LOG_DO_NOT_SEND = `none`

export type PmsCommLogRule = {
  /** The API's writeback rule type (CommLogWritebackRule.type), frozen by pmsCommLog.spec.ts. */
  key: string
  label: string
  description: string
  group: PmsCommLogGroup
  /** Only where the channel is a fact. A form did not happen over a phone, so the practice picks. */
  defaultChannel?: PmsCommLogChannel
  /** The broad rule the API falls back to when this kind has no rule of its own. */
  parent?: PmsCommLogParentKey
}

export const PMS_COMM_LOG_GROUP_LABELS: Readonly<Record<PmsCommLogGroup, string>> = {
  conversations: `Conversations`,
  records: `Records`,
}

export const PMS_COMM_LOG_GROUPS: readonly PmsCommLogGroup[] = [`conversations`, `records`]

export const PMS_COMM_LOG_RULES: readonly PmsCommLogRule[] = [
  {
    key: `textStaff`,
    label: `Texts, staff`,
    description: `Sent by a person from the inbox`,
    group: `conversations`,
    defaultChannel: `TEXT`,
    parent: `text`,
  },
  {
    key: `textAutomated`,
    label: `Texts, automated`,
    description: `Sent by a workflow, campaign or the SMS agent`,
    group: `conversations`,
    defaultChannel: `TEXT`,
    parent: `text`,
  },
  {
    key: `textPatient`,
    label: `Texts, patient replies`,
    description: `Received from the patient`,
    group: `conversations`,
    defaultChannel: `TEXT`,
    parent: `text`,
  },
  {
    key: `callStaff`,
    label: `Calls, staff`,
    description: `Tracked calls handled by the office`,
    group: `conversations`,
    defaultChannel: `PHONE`,
    parent: `call`,
  },
  {
    key: `callAiAgent`,
    label: `Calls, AI agent`,
    description: `Calls the voice agent handled`,
    group: `conversations`,
    defaultChannel: `PHONE`,
    parent: `call`,
  },
  {
    key: `emailStaff`,
    label: `Emails, staff`,
    description: `Sent by a person from the inbox`,
    group: `conversations`,
    defaultChannel: `EMAIL`,
    parent: `email`,
  },
  {
    key: `emailAutomated`,
    label: `Emails, automated`,
    description: `Sent by a workflow or campaign`,
    group: `conversations`,
    defaultChannel: `EMAIL`,
    parent: `email`,
  },
  {
    key: `form`,
    label: `Form submissions`,
    description: `Form name and the answers given`,
    group: `records`,
  },
  {
    key: `scheduler`,
    label: `Online scheduler bookings`,
    description: `Appointments the patient booked online`,
    group: `records`,
  },
  {
    key: `followUp`,
    label: `Follow-ups`,
    description: `Follow-up outcomes logged in Trackable`,
    group: `records`,
  },
  {
    key: `note`,
    label: `Notes`,
    description: `Notes staff typed on the contact`,
    group: `records`,
  },
  {
    key: `task`,
    label: `Tasks`,
    description: `Tasks on the contact`,
    group: `records`,
  },
]

export type PmsCommLogMap = Record<string, PmsCommLogChannel>

const PARENT_KEYS: ReadonlySet<string> = new Set(
  PMS_COMM_LOG_RULES.flatMap((rule) => (rule.parent ? [rule.parent] : [])),
)

/**
 * What each row writes today. A kind with no rule of its own still writes under its broad text, call or
 * email rule (the API falls back to it), so the row shows that channel. A location with no rules opens on
 * the conversation defaults, so the common case is one Save.
 */
export function effectiveChannels(saved: readonly PmsCommLogSavedRule[]): PmsCommLogMap {
  const byType = new Map(saved.map((rule) => [rule.type, rule.channel]))
  const channels: PmsCommLogMap = {}
  for (const rule of PMS_COMM_LOG_RULES) {
    const channel = byType.get(rule.key) ?? (rule.parent ? byType.get(rule.parent) : undefined)
    if (saved.length === 0 && rule.defaultChannel) channels[rule.key] = rule.defaultChannel
    else if (channel && (PMS_COMM_LOG_CHANNELS as readonly string[]).includes(channel))
      channels[rule.key] = channel as PmsCommLogChannel
  }
  return channels
}

/**
 * The rule writes that make the API match the rows. Every kind gets its own rule, and the broad text, call
 * and email rules are removed: left in place they would keep writing a kind the practice set to Do not send.
 * Rule types this build does not know stay as they are.
 */
export function planRuleChanges(saved: readonly PmsCommLogSavedRule[], selected: PmsCommLogMap): PmsCommLogRulePlan {
  const plan: PmsCommLogRulePlan = { create: [], update: [], remove: [] }
  const byType = new Map(saved.map((rule) => [rule.type, rule]))
  for (const rule of PMS_COMM_LOG_RULES) {
    const existing = byType.get(rule.key)
    const channel = selected[rule.key]
    if (!channel) {
      if (existing) plan.remove.push(existing.id)
    } else if (!existing) {
      plan.create.push({ type: rule.key, channel })
    } else if (existing.channel !== channel) {
      plan.update.push({ id: existing.id, channel })
    }
  }
  for (const rule of saved) {
    if (PARENT_KEYS.has(rule.type)) plan.remove.push(rule.id)
  }
  return plan
}
